import type {
  IBiomaterial, IEvent, PatientTimelineEventKind,
} from '@zero-dash/types';
import { getPatientTimelineEventKind, getSpecimenPurposeAbbreviation } from '@zero-dash/types';
import dayjs from 'dayjs';
import {
  DAY_MS,
  DEFAULT_MAX_TICK_COLUMNS,
  FALLBACK_MIN_VIEW_WIDTH,
  MARKER_DOT_WIDTH,
  MARKER_LABEL_GAP,
  MARKER_LABEL_MAX_WIDTH,
  MARKER_LABEL_MIN_WIDTH,
  MIN_VIEW_DAY_SPAN,
  MONTH_TICK_MIN_WIDTH,
  SAMPLE_TRACKS,
  TIMELINE_COLOURS,
  VISIBLE_EVENT_KINDS,
} from '@/constants/PatientDashboard/patientTimeline';
import { corePalette } from '@/themes/colours';
import { reportOptions } from '@/constants/Reports/reports';
import type { IBiosample } from '@/types/Analysis/Biosamples.types';
import type { IReport, ReportType } from '@/types/Reports/Reports.types';
import type { CaseStatus } from '@/types/TaskDashboard/TaskDashboard.types';
import { mapReportStatus } from '@/utils/components/taskdashboard/mapReportStatus';
import { getAssayLabel } from '@/utils/functions/getAssayLabel';
import mapEvent from '@/utils/functions/mapEvent';
import type {
  DatedReport,
  IAxisTick,
  IDateRange,
  IPatientTimelineReportVersion,
  IReportTimelineMarker,
  ISampleTrack,
  ITimelineData,
  ITimelineMarker,
  ViewWindow,
} from '@/types/PatientDashboard/PatientTimeline.types';

const NO_EVENT_CODE = '';
const DEFAULT_EVENT_COLOUR = corePalette.grey100;

interface IEventKindMarker {
  kind: PatientTimelineEventKind;
  code: string;
  colour: string;
  /** A one-off event happens once per patient, so its eventNumber carries no sequence. */
  isOneOff?: boolean;
}

const EVENT_KIND_MARKERS: readonly IEventKindMarker[] = [
  { kind: 'Diagnosis', code: 'D', colour: corePalette.blue150 },
  { kind: 'Relapse', code: 'R', colour: corePalette.blue300 },
  { kind: 'Progression', code: 'P', colour: corePalette.orange150 },
  {
    kind: 'Registration', code: 'Reg', colour: corePalette.green200, isOneOff: true,
  },
  {
    kind: 'Death', code: 'Death', colour: corePalette.offBlack100, isOneOff: true,
  },
];

const getEventKindMarker = (
  eventKind: PatientTimelineEventKind,
): IEventKindMarker | undefined => EVENT_KIND_MARKERS.find(({ kind }) => kind === eventKind);

export const getValidDate = (date?: string | null): dayjs.Dayjs | null => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate;
};

export const formatDisplayDate = (date: string): string => {
  const parsedDate = getValidDate(date);
  return parsedDate?.format('D MMM YYYY') ?? date;
};

export const getEventSequenceNumber = (eventNumber?: string): string => {
  const normalisedEventNumber = eventNumber?.trim();
  if (!normalisedEventNumber) {
    return '';
  }

  return normalisedEventNumber.match(/([0-9]+)$/)?.[1] ?? normalisedEventNumber;
};

export const getEventCode = (eventKind: PatientTimelineEventKind): string => (
  getEventKindMarker(eventKind)?.code ?? NO_EVENT_CODE
);

export const getEventColour = (eventKind: PatientTimelineEventKind): string => (
  getEventKindMarker(eventKind)?.colour ?? DEFAULT_EVENT_COLOUR
);

export const getEventMarkerCode = (
  eventKind: PatientTimelineEventKind,
  eventNumber?: string,
): string => {
  const marker = getEventKindMarker(eventKind);
  const code = marker?.code ?? NO_EVENT_CODE;
  if (marker?.isOneOff) {
    return code;
  }

  return `${code}${getEventSequenceNumber(eventNumber)}`.trim();
};

export const getEventPopupLabel = (
  label: string,
  eventKind: PatientTimelineEventKind,
): string => `${label}(${mapEvent(eventKind)})`;

export const getReportLabel = (type: ReportType): string => (
  reportOptions.find((option) => option.value === type)?.name ?? type
);

export const isDisplayableReportType = (type: ReportType): boolean => (
  reportOptions.some((option) => option.value === type)
);

export const getReportAbbreviation = (type: ReportType): string => {
  const option = reportOptions.find((item) => item.value === type);
  if (option?.abbreviation) {
    return option.abbreviation;
  }
  return type === 'PRECLINICAL_REPORT' ? 'P' : (option?.name ?? type);
};

export const getReportShortLabel = (type: ReportType): string => (
  getReportLabel(type).replace(/\s+Report$/, '')
);

export const buildReportVersionHref = (
  patientId: string,
  type: ReportType,
  version: IPatientTimelineReportVersion,
): string => (
  `/${patientId}/${version.analysisSetId}/reports?reportId=${version.reportId}&reportType=${type}`
);

export const buildCurationHref = (patientId: string, analysisSetId: string): string => (
  `/${patientId}/${analysisSetId}/curation`
);

/**
 * `isGermlineOnly` and `isHighRisk` only steer `mapReportStatus` when the report has no
 * status at all, and `IReport.status` is always populated, so passing false for both
 * cannot change the result here.
 */
export const getReportCaseStatus = (report: IReport): CaseStatus => (
  mapReportStatus(report.type, report.pseudoStatus || report.status, false, false)
);

export const getReportVersionsById = (
  reports: DatedReport[],
): Map<string, IPatientTimelineReportVersion> => {
  const reportsByType = new Map<ReportType, DatedReport[]>();
  reports.forEach((report) => {
    reportsByType.set(report.type, [...(reportsByType.get(report.type) ?? []), report]);
  });

  const versionsById = new Map<string, IPatientTimelineReportVersion>();
  reportsByType.forEach((typeReports) => {
    [...typeReports]
      .sort((a, b) => (
        a.approvedAt.localeCompare(b.approvedAt)
        || dayjs(a.createdAt).diff(dayjs(b.createdAt))
      ))
      .forEach((report, index) => {
        versionsById.set(report.id, {
          reportId: report.id,
          analysisSetId: report.analysisSetId,
          version: index + 1,
          approvedAt: report.approvedAt,
          caseStatus: getReportCaseStatus(report),
        });
      });
  });

  return versionsById;
};

interface IDatedReportEntry {
  report: DatedReport;
  date: string;
}

export const buildReportMarkers = (reports: IReport[]): IReportTimelineMarker[] => {
  const datedReports = reports
    .map((report) => {
      const { approvedAt } = report;
      if (!approvedAt || !isDisplayableReportType(report.type)) {
        return null;
      }

      const approvedDate = getValidDate(approvedAt);
      return approvedDate
        ? { report: { ...report, approvedAt }, date: approvedDate.format('YYYY-MM-DD') }
        : null;
    })
    .filter((entry): entry is IDatedReportEntry => entry !== null);

  const reportsByTypeAndDate = new Map<string, IDatedReportEntry[]>();
  datedReports.forEach((entry) => {
    const key = `${entry.report.type}__${entry.date}`;
    reportsByTypeAndDate.set(key, [...(reportsByTypeAndDate.get(key) ?? []), entry]);
  });

  const versionsById = getReportVersionsById(datedReports.map((entry) => entry.report));
  const getMarkerVersions = (
    entries: IDatedReportEntry[],
  ): IPatientTimelineReportVersion[] => (
    entries
      .map((entry) => versionsById.get(entry.report.id))
      .filter((version): version is IPatientTimelineReportVersion => version !== undefined)
      .sort((a, b) => b.version - a.version)
  );

  return Array.from(reportsByTypeAndDate.values()).map((entries) => {
    // Order by approval time so the grouped marker is deterministic.
    const sorted = [...entries].sort(
      (a, b) => a.report.approvedAt.localeCompare(b.report.approvedAt),
    );
    const { report, date } = sorted[0];
    const { type } = report;
    const abbreviation = getReportAbbreviation(type);

    if (sorted.length === 1) {
      return {
        id: `report-${report.id}`,
        date,
        label: abbreviation,
        colour: corePalette.grey100,
        reportTrackId: type,
        reportVersions: getMarkerVersions(sorted),
      };
    }

    return {
      id: `report-${type}-${date}-group-${sorted.length}`,
      date,
      label: abbreviation,
      colour: corePalette.grey100,
      reportTrackId: type,
      groupCount: sorted.length,
      stackItemIds: sorted.map((entry) => entry.report.id),
      reportVersions: getMarkerVersions(sorted),
    };
  });
};

export const getSampleTrackId = (
  biomaterial: IBiomaterial,
): ISampleTrack['id'] | null => {
  switch (biomaterial.specimenPurpose) {
    case 'Tumour':
    case 'TB Banked':
    case 'TB ZERO Potentials':
      return 'tumour';
    case 'Liquid Biopsy':
      return 'lb';
    case 'Germline - patient':
      return 'germline';
    case 'Germline - donor':
      return null;
    default:
      return 'unknown';
  }
};

export const getSampleAssayLabel = (biomaterial: IBiomaterial): string => (
  getAssayLabel(biomaterial.downstreamProcess)
);

export const getSampleLabel = (biomaterial: IBiomaterial): string => {
  const sampleAbbreviation = getSpecimenPurposeAbbreviation(biomaterial.specimenPurpose);
  const assayLabel = getSampleAssayLabel(biomaterial);

  return assayLabel ? `${sampleAbbreviation} - ${assayLabel}` : sampleAbbreviation;
};

export const groupSampleMarkers = (markers: ITimelineMarker[]): ITimelineMarker[] => {
  const groups = new Map<string, ITimelineMarker[]>();
  markers.forEach((marker) => {
    const key = `${marker.sampleTrackId ?? ''}__${marker.date}`;
    const existingMarkers = groups.get(key) ?? [];
    groups.set(key, [...existingMarkers, marker]);
  });

  return Array.from(groups.values()).map((groupMarkers) => {
    if (groupMarkers.length === 1) {
      return groupMarkers[0];
    }

    const parentMarkers = groupMarkers.filter((marker) => marker.parentId == null);
    const childMarkers = groupMarkers.filter((marker) => marker.parentId != null);
    const orderedMarkers = [...parentMarkers, ...childMarkers];
    const baseMarker = orderedMarkers[0];
    const primaryMarker = orderedMarkers.find((marker) => marker.hasAssay) ?? baseMarker;
    return {
      ...baseMarker,
      id: `${baseMarker.id}-group-${orderedMarkers.length}`,
      label: primaryMarker.label,
      hasAssay: primaryMarker.hasAssay,
      biomaterialIds: orderedMarkers.flatMap((marker) => marker.biomaterialIds ?? []),
      groupCount: orderedMarkers.length,
      groupedSamples: orderedMarkers.flatMap((marker) => marker.groupedSamples ?? []),
    };
  });
};

let labelMeasureContext: CanvasRenderingContext2D | null | undefined;

const measureLabelWidth = (text: string): number => {
  if (labelMeasureContext === undefined) {
    labelMeasureContext = typeof document !== 'undefined'
      ? document.createElement('canvas').getContext('2d')
      : null;
    if (labelMeasureContext) {
      labelMeasureContext.font = '500 12px Roboto, sans-serif';
    }
  }

  if (!labelMeasureContext) {
    return text.length * 7;
  }

  return labelMeasureContext.measureText(text).width;
};

export const computeMarkerLabelWidths = (
  markers: ITimelineMarker[],
  xPct: (date: string) => number,
  contentWidth: number,
): Map<string, number | null> => {
  const widths = new Map<string, number | null>();

  if (contentWidth <= 0) {
    markers.forEach((marker) => widths.set(marker.id, MARKER_LABEL_MAX_WIDTH));
    return widths;
  }

  const positioned = markers
    .map((marker) => ({ marker, left: xPct(marker.date) }))
    .sort((a, b) => a.left - b.left);

  positioned.forEach(({ marker, left }, index) => {
    const nextLeft = positioned[index + 1]?.left ?? 100;
    const gapPx = ((nextLeft - left) / 100) * contentWidth;
    const countReserve = marker.groupCount && marker.groupCount > 1
      ? Math.ceil(measureLabelWidth(`+${marker.groupCount - 1}`)) + MARKER_LABEL_GAP
      : 0;
    const available = gapPx - MARKER_DOT_WIDTH - MARKER_LABEL_GAP - countReserve;
    const labelWidth = Math.ceil(measureLabelWidth(marker.label));
    const minToShow = Math.min(MARKER_LABEL_MIN_WIDTH, labelWidth);
    widths.set(
      marker.id,
      available >= minToShow ? Math.min(available, MARKER_LABEL_MAX_WIDTH) : null,
    );
  });

  return widths;
};

export const orderTrackRows = <T extends { id: string }>(
  rows: T[],
  order: string[],
): T[] => {
  const rowMap = new Map(rows.map((row) => [row.id, row]));
  const orderedRows = order
    .map((id) => rowMap.get(id))
    .filter((row): row is T => row !== undefined);
  const orderedIds = new Set(order);
  const unorderedRows = rows.filter((row) => !orderedIds.has(row.id));

  return [...orderedRows, ...unorderedRows];
};

export const reorderTrackIds = (
  ids: string[],
  sourceId: string,
  targetId: string,
): string[] => {
  const sourceIndex = ids.indexOf(sourceId);
  const targetIndex = ids.indexOf(targetId);

  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
    return ids;
  }

  const nextIds = [...ids];
  const [movedId] = nextIds.splice(sourceIndex, 1);
  nextIds.splice(targetIndex, 0, movedId);

  return nextIds;
};

export const getDateRange = (dates: string[]): IDateRange => {
  const values = dates
    .map((date) => getValidDate(date)?.valueOf())
    .filter((value): value is number => value !== undefined);

  if (!values.length) {
    const fallbackStart = dayjs().startOf('month');
    return {
      startMs: fallbackStart.subtract(1, 'month').valueOf(),
      endMs: fallbackStart.add(5, 'month').valueOf(),
    };
  }

  const minDate = dayjs(Math.min(...values)).subtract(14, 'day').startOf('day');
  const maxDate = dayjs(Math.max(...values)).add(14, 'day').endOf('day');

  if (maxDate.diff(minDate, 'day') < 30) {
    return {
      startMs: minDate.subtract(15, 'day').valueOf(),
      endMs: maxDate.add(30, 'day').valueOf(),
    };
  }

  return {
    startMs: minDate.valueOf(),
    endMs: maxDate.valueOf(),
  };
};

export const dateToFraction = (date: string, range: IDateRange): number => {
  const parsedDate = getValidDate(date);
  if (!parsedDate) {
    return 0;
  }

  const total = range.endMs - range.startMs;
  if (total <= 0) {
    return 0;
  }

  return (parsedDate.valueOf() - range.startMs) / total;
};

export const getMinViewWidth = (range: IDateRange): number => {
  const totalDays = (range.endMs - range.startMs) / DAY_MS;

  if (totalDays <= MIN_VIEW_DAY_SPAN) {
    return 1;
  }

  return Math.min(1, MIN_VIEW_DAY_SPAN / totalDays);
};

export const clampView = (
  view: ViewWindow,
  minViewWidth = FALLBACK_MIN_VIEW_WIDTH,
): ViewWindow => {
  const [viewStart, viewEnd] = view;
  const width = Math.min(1, Math.max(minViewWidth, viewEnd - viewStart));
  const start = Math.max(0, Math.min(1 - width, viewStart));

  return [start, start + width];
};

export const getTickTargetCount = (axisWidth: number): number => {
  if (!axisWidth) {
    return DEFAULT_MAX_TICK_COLUMNS;
  }

  const countByWidth = Math.floor(axisWidth / MONTH_TICK_MIN_WIDTH);

  return Math.max(2, Math.min(DEFAULT_MAX_TICK_COLUMNS, countByWidth));
};

export const getDayTickStep = (visibleDays: number): number => {
  if (visibleDays <= 8) {
    return 1;
  }

  if (visibleDays <= 16) {
    return 2;
  }

  if (visibleDays <= 24) {
    return 3;
  }

  if (visibleDays <= 56) {
    return 7;
  }

  return 14;
};

export const limitTicks = (ticks: IAxisTick[]): IAxisTick[] => {
  if (ticks.length <= DEFAULT_MAX_TICK_COLUMNS) {
    return ticks;
  }

  return ticks.slice(0, DEFAULT_MAX_TICK_COLUMNS);
};

export const buildTicks = (
  range: IDateRange,
  view: ViewWindow,
  axisWidth = 0,
): IAxisTick[] => {
  const [viewStart, viewEnd] = view;
  const total = range.endMs - range.startMs;
  const startMs = range.startMs + viewStart * total;
  const endMs = range.startMs + viewEnd * total;
  const visibleDays = Math.max(1, (endMs - startMs) / DAY_MS);
  if (visibleDays <= 90) {
    const step = getDayTickStep(visibleDays);
    let current = dayjs(startMs).startOf('day');
    const endDate = dayjs(endMs);
    const ticks: IAxisTick[] = [];

    while (current.valueOf() <= endDate.valueOf()) {
      if (current.valueOf() >= startMs) {
        ticks.push({
          date: current.format('YYYY-MM-DD'),
          label: current.format('D MMM YYYY'),
          isDayLevel: true,
        });
      }
      current = current.add(step, 'day');
    }

    return limitTicks(ticks);
  }

  let current = dayjs(startMs).startOf('month');
  if (current.valueOf() < startMs) {
    current = current.add(1, 'month');
  }
  const endDate = dayjs(endMs);
  const monthTicks: IAxisTick[] = [];
  while (current.valueOf() <= endDate.valueOf()) {
    monthTicks.push({
      date: current.format('YYYY-MM-DD'),
      label: current.format('MMM YYYY'),
      isDayLevel: false,
    });
    current = current.add(1, 'month');
  }

  const targetCount = getTickTargetCount(axisWidth);
  const step = Math.max(1, Math.ceil(monthTicks.length / targetCount));

  return limitTicks(monthTicks.filter((tick, index) => index % step === 0));
};

interface IBuildTimelineDataParams {
  events?: IEvent[];
  biomaterials?: IBiomaterial[];
  biosamples?: IBiosample[];
  reports?: IReport[];
  registrationDate?: string;
  firstCollection?: string;
}

export const buildTimelineData = ({
  events,
  biomaterials,
  biosamples,
  reports,
  registrationDate,
  firstCollection,
}: IBuildTimelineDataParams): ITimelineData => {
  const eventMarkers = (events ?? [])
    .map((event, index): ITimelineData['eventMarkers'][number] | null => {
      const eventDate = getValidDate(event.date);
      if (!eventDate) {
        return null;
      }

      const eventKind = getPatientTimelineEventKind(event.event);
      if (!VISIBLE_EVENT_KINDS.includes(eventKind)) {
        return null;
      }

      return {
        id: `event-${event.eventNumber}-${event.date}-${index}`,
        date: eventDate.format('YYYY-MM-DD'),
        label: getEventMarkerCode(eventKind, event.eventNumber),
        colour: getEventColour(eventKind),
        eventKind,
      };
    })
    .filter((marker): marker is ITimelineData['eventMarkers'][number] => marker !== null);

  const registrationMarker = getValidDate(registrationDate);
  if (registrationMarker) {
    eventMarkers.unshift({
      id: 'event-registration',
      date: registrationMarker.format('YYYY-MM-DD'),
      label: getEventCode('Registration'),
      colour: getEventColour('Registration'),
      eventKind: 'Registration',
    });
  }

  const rawSampleMarkers = (biomaterials ?? [])
    .map((biomaterial): ITimelineData['sampleMarkers'][number] | null => {
      const collectionDate = getValidDate(biomaterial.collectionDate);
      if (!collectionDate) {
        return null;
      }

      const assayDisplay = getSampleAssayLabel(biomaterial);
      const label = getSampleLabel(biomaterial);
      const sampleTrackId = getSampleTrackId(biomaterial);
      if (!sampleTrackId) {
        // Germline - donor is not shown on the timeline.
        return null;
      }
      const colour = TIMELINE_COLOURS.sampleDot;
      const subtype = biomaterial.subtype || 'Not set';

      return {
        id: `sample-${biomaterial.id}`,
        date: collectionDate.format('YYYY-MM-DD'),
        label,
        colour,
        parentId: biomaterial.parentId,
        biomaterialIds: [biomaterial.id],
        hasAssay: Boolean(assayDisplay),
        groupedSamples: [
          {
            assay: assayDisplay || 'Not set',
            id: String(biomaterial.id),
            name: biomaterial.name,
            subtype,
            isParent: biomaterial.parentId == null,
          },
        ],
        sampleTrackId,
      };
    })
    .filter((marker): marker is ITimelineData['sampleMarkers'][number] => marker !== null);
  const sampleMarkers = groupSampleMarkers(rawSampleMarkers);

  const sampleTracks: ISampleTrack[] = SAMPLE_TRACKS
    .map((definition) => ({
      ...definition,
      markers: sampleMarkers.filter((marker) => marker.sampleTrackId === definition.id),
    }))
    .filter((track) => track.markers.length > 0);

  const reportMarkers = buildReportMarkers(reports ?? []);

  const range = getDateRange([
    registrationDate,
    firstCollection,
    ...(events ?? []).map((event) => event.date),
    ...(biomaterials ?? []).map((biomaterial) => biomaterial.collectionDate),
    ...(biosamples ?? []).map((biosample) => biosample.collectionDate),
    ...(reports ?? []).map((report) => report.approvedAt),
  ].filter((date): date is string => Boolean(date)));

  return {
    range,
    eventMarkers,
    sampleMarkers,
    sampleTracks,
    reportMarkers,
  };
};
